/**
 * Royal Dastinos MUI theme.
 *
 * Extracted from App.js so App stays focused on routing. All colors come from
 * ./tokens. Typography uses scalar font sizes + responsiveFontSizes() (the old
 * `{ xs, sm, ... }` objects inside createTheme were invalid and did nothing).
 */

import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { colors, fonts, fontWeights, radius } from './tokens';

let theme = createTheme({
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },

  palette: {
    primary: {
      main: colors.terracotta,       // AA-compliant behind white text
      light: colors.terracottaLight,
      dark: colors.terracottaDark,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.sand,
      light: colors.sandLight,
      dark: colors.sandDark,
      contrastText: colors.ink,
    },
    // Accent kept available for decorative use (never behind white text).
    accent: {
      main: colors.terracottaAccent,
      contrastText: colors.ink,
    },
    success: { main: colors.success, light: colors.successLight, dark: colors.successDark },
    info: { main: colors.info, light: colors.infoLight, dark: colors.infoDark },
    warning: { main: colors.warning, light: colors.warningLight, dark: colors.warningDark },
    error: { main: colors.error, light: colors.errorLight, dark: colors.errorDark },
    background: { default: colors.background, paper: colors.paper },
    text: { primary: colors.ink, secondary: colors.inkSoft },
    divider: colors.divider,
  },

  typography: {
    fontFamily: fonts.body,
    h1: { fontFamily: fonts.heading, fontWeight: fontWeights.bold, letterSpacing: '-0.01562em', lineHeight: 1.2, fontSize: '3.5rem' },
    h2: { fontFamily: fonts.heading, fontWeight: fontWeights.semibold, letterSpacing: '-0.00833em', lineHeight: 1.3, fontSize: '2.75rem' },
    h3: { fontFamily: fonts.heading, fontWeight: fontWeights.semibold, letterSpacing: '0em', lineHeight: 1.3, fontSize: '2.25rem' },
    h4: { fontFamily: fonts.heading, fontWeight: fontWeights.semibold, letterSpacing: '0.00735em', lineHeight: 1.4, fontSize: '1.85rem' },
    h5: { fontFamily: fonts.body, fontWeight: fontWeights.semibold, letterSpacing: '0em', lineHeight: 1.5, fontSize: '1.4rem' },
    h6: { fontFamily: fonts.body, fontWeight: fontWeights.medium, letterSpacing: '0.0075em', lineHeight: 1.6, fontSize: '1.15rem' },
    subtitle1: { fontFamily: fonts.body, letterSpacing: '0.00938em', lineHeight: 1.6, fontSize: '1rem' },
    subtitle2: { fontFamily: fonts.body, fontWeight: fontWeights.medium, letterSpacing: '0.00714em', lineHeight: 1.6, fontSize: '0.9rem' },
    body1: { fontFamily: fonts.body, letterSpacing: '0.00938em', lineHeight: 1.7, fontSize: '1rem' },
    body2: { fontFamily: fonts.body, letterSpacing: '0.01071em', lineHeight: 1.6, fontSize: '0.875rem' },
    button: { fontFamily: fonts.body, textTransform: 'none', fontWeight: fontWeights.semibold, letterSpacing: '0.02857em', fontSize: '0.95rem' },
    caption: { fontFamily: fonts.body, letterSpacing: '0.03333em', lineHeight: 1.4, fontSize: '0.75rem' },
    overline: { fontFamily: fonts.body, fontWeight: fontWeights.medium, letterSpacing: '0.08333em', lineHeight: 1.4, textTransform: 'uppercase', fontSize: '0.75rem' },
  },

  shape: { borderRadius: radius },

  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.05),0px 1px 1px 0px rgba(0,0,0,0.04),0px 1px 3px 0px rgba(0,0,0,0.03)',
    '0px 3px 1px -2px rgba(0,0,0,0.05),0px 2px 2px 0px rgba(0,0,0,0.04),0px 1px 5px 0px rgba(0,0,0,0.03)',
    '0px 3px 3px -2px rgba(0,0,0,0.05),0px 3px 4px 0px rgba(0,0,0,0.04),0px 1px 8px 0px rgba(0,0,0,0.03)',
    '0px 2px 4px -1px rgba(0,0,0,0.05),0px 4px 5px 0px rgba(0,0,0,0.04),0px 1px 10px 0px rgba(0,0,0,0.03)',
    '0px 3px 5px -1px rgba(0,0,0,0.05),0px 5px 8px 0px rgba(0,0,0,0.04),0px 1px 14px 0px rgba(0,0,0,0.03)',
    '0px 3px 5px -1px rgba(0,0,0,0.05),0px 6px 10px 0px rgba(0,0,0,0.04),0px 1px 18px 0px rgba(0,0,0,0.03)',
    '0px 4px 5px -2px rgba(0,0,0,0.05),0px 7px 10px 1px rgba(0,0,0,0.04),0px 2px 16px 1px rgba(0,0,0,0.03)',
    '0px 5px 5px -3px rgba(0,0,0,0.05),0px 8px 10px 1px rgba(0,0,0,0.04),0px 3px 14px 2px rgba(0,0,0,0.03)',
    '0px 5px 6px -3px rgba(0,0,0,0.05),0px 9px 12px 1px rgba(0,0,0,0.04),0px 3px 16px 2px rgba(0,0,0,0.03)',
    '0px 6px 6px -3px rgba(0,0,0,0.05),0px 10px 14px 1px rgba(0,0,0,0.04),0px 4px 18px 3px rgba(0,0,0,0.03)',
    '0px 6px 7px -4px rgba(0,0,0,0.05),0px 11px 15px 1px rgba(0,0,0,0.04),0px 4px 20px 3px rgba(0,0,0,0.03)',
    '0px 7px 8px -4px rgba(0,0,0,0.05),0px 12px 17px 2px rgba(0,0,0,0.04),0px 5px 22px 4px rgba(0,0,0,0.03)',
    '0px 7px 8px -4px rgba(0,0,0,0.05),0px 13px 19px 2px rgba(0,0,0,0.04),0px 5px 24px 4px rgba(0,0,0,0.03)',
    '0px 7px 9px -4px rgba(0,0,0,0.05),0px 14px 21px 2px rgba(0,0,0,0.04),0px 5px 26px 4px rgba(0,0,0,0.03)',
    '0px 8px 9px -5px rgba(0,0,0,0.05),0px 15px 22px 2px rgba(0,0,0,0.04),0px 6px 28px 5px rgba(0,0,0,0.03)',
    '0px 8px 10px -5px rgba(0,0,0,0.05),0px 16px 24px 2px rgba(0,0,0,0.04),0px 6px 30px 5px rgba(0,0,0,0.03)',
    '0px 8px 11px -5px rgba(0,0,0,0.05),0px 17px 26px 2px rgba(0,0,0,0.04),0px 6px 32px 5px rgba(0,0,0,0.03)',
    '0px 9px 11px -5px rgba(0,0,0,0.05),0px 18px 28px 2px rgba(0,0,0,0.04),0px 7px 34px 6px rgba(0,0,0,0.03)',
    '0px 9px 12px -6px rgba(0,0,0,0.05),0px 19px 29px 2px rgba(0,0,0,0.04),0px 7px 36px 6px rgba(0,0,0,0.03)',
    '0px 10px 13px -6px rgba(0,0,0,0.05),0px 20px 31px 3px rgba(0,0,0,0.04),0px 8px 38px 7px rgba(0,0,0,0.03)',
    '0px 10px 13px -6px rgba(0,0,0,0.05),0px 21px 33px 3px rgba(0,0,0,0.04),0px 8px 40px 7px rgba(0,0,0,0.03)',
    '0px 10px 14px -6px rgba(0,0,0,0.05),0px 22px 35px 3px rgba(0,0,0,0.04),0px 8px 42px 7px rgba(0,0,0,0.03)',
    '0px 11px 14px -7px rgba(0,0,0,0.05),0px 23px 36px 3px rgba(0,0,0,0.04),0px 9px 44px 8px rgba(0,0,0,0.03)',
    '0px 11px 15px -7px rgba(0,0,0,0.05),0px 24px 38px 3px rgba(0,0,0,0.04),0px 9px 46px 8px rgba(0,0,0,0.03)',
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { scrollBehavior: 'smooth', WebkitTapHighlightColor: 'transparent' },
        html: { fontSize: '16px' },
        '@media (max-width:600px)': { html: { fontSize: '14px' } },
        '@media (min-width:601px) and (max-width:960px)': { html: { fontSize: '15px' } },
        img: { maxWidth: '100%', height: 'auto' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius,
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.1)' },
        },
        outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
        // Solid fills (not background-image gradients) so component-level bgcolor
        // overrides compose correctly and can never bleed orange-on-orange.
        containedPrimary: {
          backgroundColor: colors.terracotta,
          color: '#fff',
          '&:hover': { backgroundColor: colors.terracottaDark },
        },
        containedSecondary: {
          backgroundColor: colors.sand,
          color: colors.ink,
          '&:hover': { backgroundColor: colors.sandDark },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius,
          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          transition: 'transform 0.5s ease, box-shadow 0.5s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: radius },
        elevation1: { boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
        elevation2: { boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
        elevation3: { boxShadow: '0 6px 16px rgba(0,0,0,0.05)' },
        elevation4: { boxShadow: '0 8px 20px rgba(0,0,0,0.05)' },
      },
    },
    MuiAppBar: {
      styleOverrides: { root: { boxShadow: '0 2px 10px rgba(0,0,0,0.05)' } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius,
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.terracottaAccent },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
        },
        notchedOutline: { borderColor: 'rgba(0, 0, 0, 0.15)', transition: 'border-color 0.3s ease' },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          position: 'relative',
          '&:hover': { textDecoration: 'none' },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: '0%',
            height: '2px',
            bottom: 0,
            left: 0,
            backgroundColor: colors.terracottaAccent,
            transition: 'width 0.3s ease',
          },
          '&:hover::after': { width: '100%' },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: radius } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: radius } } },
    MuiAlert: { styleOverrides: { root: { borderRadius: radius } } },
    MuiCardMedia: { styleOverrides: { root: { transition: 'transform 0.7s ease' } } },
    MuiAvatar: { styleOverrides: { root: { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' } } },
  },
});

theme = responsiveFontSizes(theme);

export default theme;
